import type * as core from '../../../../../../../core'

const rowOnClick = (variables: {
  jiraStatusFilter?: string | null
  typeFilter?: string | null
  assigneeFilter?: string | null
}) => ({
  '{}': {
    action: {
      '{}': {
        payload: {
          '{}': {
            goto: {
              '{}': {
                variables: {
                  '{}': {
                    jiraStatusFilter: variables.jiraStatusFilter ?? null,
                    typeFilter: variables.typeFilter ?? null,
                    assigneeFilter: variables.assigneeFilter ?? null,
                    dueDateFilter: null,
                  },
                },
                navigationId: 'requirements-dashboard',
              },
            },
          },
        },
        operation: 'ui.navigate',
      },
    },
  },
})

export const query: core.mosaic.Widget.Query = {
  selection: {
    '{}': {
      name: 'Requirements Summary',
      type: 'report-card',
      content: {
        '[]': [
          {
            '{}': {
              name: 'Total',
              onClick: rowOnClick({}),
              values: {
                '[]': [
                  {
                    '{}': {
                      value: {
                        '{}': {
                          quality: 'neutral',
                          tooltip:
                            'Every requirement captured across all specs, regardless of Jira status or assignee.',
                          emphasis: {
                            'fmt.num': [
                              {
                                '#': {
                                  '@': 'allRequirements',
                                },
                              },
                              {
                                ',': true,
                                '.': {
                                  always: 0,
                                },
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
          {
            '{}': {
              name: 'Completion Rate',
              values: {
                '[]': [
                  {
                    '{}': {
                      value: {
                        '{}': {
                          quality: 'positive',
                          tooltip:
                            "The share of requirements whose Jira status is exactly 'Done'.",
                          emphasis: {
                            'fmt.num': [
                              {
                                if: [
                                  [
                                    {
                                      '>': [
                                        {
                                          '#': {
                                            '@': 'allRequirements',
                                          },
                                        },
                                        0,
                                      ],
                                    },
                                    {
                                      '/': [
                                        {
                                          '#': {
                                            '@': 'doneRequirements',
                                          },
                                        },
                                        {
                                          '#': {
                                            '@': 'allRequirements',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                  0,
                                ],
                              },
                              {
                                ',': true,
                                '.': {
                                  always: 0,
                                },
                                scalar: 100,
                                suffix: '%',
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
          {
            '{}': {
              name: 'Missing Spec',
              values: {
                '[]': [
                  {
                    '{}': {
                      value: {
                        '{}': {
                          quality: 'bad',
                          tooltip:
                            'No spec has been traced to this requirement yet.',
                          emphasis: {
                            'fmt.num': [
                              {
                                '#': {
                                  '@': 'missingSpecRequirements',
                                },
                              },
                              {
                                ',': true,
                                '.': {
                                  always: 0,
                                },
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
          {
            '{}': {
              name: 'Missing Type',
              values: {
                '[]': [
                  {
                    '{}': {
                      value: {
                        '{}': {
                          quality: 'bad',
                          tooltip:
                            'This requirement has not yet been categorized by type (doc, data, interface, operational, security, or functional).',
                          emphasis: {
                            'fmt.num': [
                              {
                                '#': {
                                  '@': 'missingTypeRequirements',
                                },
                              },
                              {
                                ',': true,
                                '.': {
                                  always: 0,
                                },
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
          {
            '{}': {
              name: 'Unassigned',
              values: {
                '[]': [
                  {
                    '{}': {
                      value: {
                        '{}': {
                          quality: 'neutral',
                          tooltip:
                            "The requirement's Jira issue has no employee assigned, regardless of its status.",
                          emphasis: {
                            'fmt.num': [
                              {
                                '#': {
                                  '@': 'unassignedRequirements',
                                },
                              },
                              {
                                ',': true,
                                '.': {
                                  always: 0,
                                },
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
        ],
      },
    },
  },
  subqueries: {
    allRequirements: {
      filter: {
        or: [
          { '=': [{ '@$': 'projectId' }, null] },
          {
            '=': [
              { '@': 'specProject' },
              { '@>': [{ '@$': 'projectId' }, 'recordId'] },
            ],
          },
        ],
      },
      target: 'ent:!requirements:!requirement',
      selection: {
        '@.': '!id',
      },
      subqueries: {
        specProject: {
          ref: { attr: 'attr:ent#!requirements#!requirement:!spec' },
          target: 'ent:!specs:!spec',
          selection: { '@.': ['!project', { ref: { entity: '!proj' } }] },
        },
      },
    },
    doneRequirements: {
      filter: {
        and: [
          {
            or: [
              { '=': [{ '@$': 'projectId' }, null] },
              {
                '=': [
                  { '@': 'specProject' },
                  { '@>': [{ '@$': 'projectId' }, 'recordId'] },
                ],
              },
            ],
          },
          {
            '=': [
              {
                '@.': '!jira_status',
              },
              'Done',
            ],
          },
        ],
      },
      target: 'ent:!requirements:!requirement',
      selection: {
        '@.': '!id',
      },
      subqueries: {
        specProject: {
          ref: { attr: 'attr:ent#!requirements#!requirement:!spec' },
          target: 'ent:!specs:!spec',
          selection: { '@.': ['!project', { ref: { entity: '!proj' } }] },
        },
      },
    },
    missingSpecRequirements: {
      filter: {
        and: [
          {
            or: [
              { '=': [{ '@$': 'projectId' }, null] },
              {
                '=': [
                  { '@': 'specProject' },
                  { '@>': [{ '@$': 'projectId' }, 'recordId'] },
                ],
              },
            ],
          },
          {
            '=': [
              {
                '@.': '!spec',
              },
              null,
            ],
          },
        ],
      },
      target: 'ent:!requirements:!requirement',
      selection: {
        '@.': '!id',
      },
      subqueries: {
        specProject: {
          ref: { attr: 'attr:ent#!requirements#!requirement:!spec' },
          target: 'ent:!specs:!spec',
          selection: { '@.': ['!project', { ref: { entity: '!proj' } }] },
        },
      },
    },
    missingTypeRequirements: {
      filter: {
        and: [
          {
            or: [
              { '=': [{ '@$': 'projectId' }, null] },
              {
                '=': [
                  { '@': 'specProject' },
                  { '@>': [{ '@$': 'projectId' }, 'recordId'] },
                ],
              },
            ],
          },
          {
            '=': [
              {
                '@.': '!type',
              },
              null,
            ],
          },
        ],
      },
      target: 'ent:!requirements:!requirement',
      selection: {
        '@.': '!id',
      },
      subqueries: {
        specProject: {
          ref: { attr: 'attr:ent#!requirements#!requirement:!spec' },
          target: 'ent:!specs:!spec',
          selection: { '@.': ['!project', { ref: { entity: '!proj' } }] },
        },
      },
    },
    unassignedRequirements: {
      filter: {
        and: [
          {
            or: [
              { '=': [{ '@$': 'projectId' }, null] },
              {
                '=': [
                  { '@': 'specProject' },
                  { '@>': [{ '@$': 'projectId' }, 'recordId'] },
                ],
              },
            ],
          },
          {
            '=': [
              {
                '@.': '!assignee',
              },
              null,
            ],
          },
        ],
      },
      target: 'ent:!requirements:!requirement',
      selection: {
        '@.': '!id',
      },
      subqueries: {
        specProject: {
          ref: { attr: 'attr:ent#!requirements#!requirement:!spec' },
          target: 'ent:!specs:!spec',
          selection: { '@.': ['!project', { ref: { entity: '!proj' } }] },
        },
      },
    },
  },
}
